import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';
import { getValidationRules } from '../../helpers/helper';

function stateFromProps(member = {}) {
  const softSkills = member.softSkills || {};
  return {
    languages: (softSkills.languages || []).map(l => l.code),
  };
}

/**
 * Form to update the current logged user
 */
function LanguagesForm({ t, member, onMemberChange, languages, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'languagesForm';
  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.LANGUAGES, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const handleSubmit = async () => {
    const params = {
      languages: state.languages.map(l => languages.find(la => la.code === l)),
    };
    onMemberChange(params, 'softSkills');
  };

  const handleRemoveOption = useCallback(
    field => value =>
      setState(prevState => ({
        ...prevState,
        [field]: prevState[field].filter(v => v !== value),
      })),
    [],
  );

  const handleChange = useCallback((field, value) => {
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].concat(value),
    }));
  }, []);

  const inputProps = useCallback(
    () => ({
      items: languages.map(l => ({ id: l.code, text: l.name })),
      multiple: true,
      onUnselect: handleRemoveOption('languages'),
    }),
    [languages],
  );

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_languages')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="languages"
              type="select"
              value={state.languages}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.LANGUAGES}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

LanguagesForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  onSkipStep: PropTypes.func,
};

LanguagesForm.defaultProps = {
  onSkipStep: null,
};

export default translate(LanguagesForm);
